// ./src/components/layouts/DashboardLayout/DashboardLayout.tsx
import React, { useState, useCallback, useMemo } from "react";
import {
  AppShell,
  Group,
  Text,
  Box,
  ActionIcon,
  Tooltip,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { useDisclosure, useWindowScroll } from "@mantine/hooks";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";

import { SiteMapItem, USER_ROLES } from "@/config";

import { canUserAccess } from "@/utils/userRoleHelpers";
import { buildUrlWithFilters } from "@/utils/filterHelpers";

import { ParkingSession } from "@/utils/api/ParkingSessionService";

import { useApp } from "@/contexts/AppContext";
import { useDashboardContext } from "@/contexts/DashboardContext";
import { useEntityFiltersContext } from "@/contexts/EntityFiltersContext";
import { useExpenseTagsContext } from "@/contexts/ExpenseTagsContext";
import { useUserNotificationsContext } from "@/contexts/UserNotificationsContext";
import { useUserProfileContext } from "@/contexts/UserProfileContext";
import { useActiveTravelContext } from "@/contexts/ActiveTravelContext";
import { useActiveParkingContext } from "@/contexts/ActiveParkingContext";
import { useLayoutUI } from "@/contexts/LayoutUIContext";
import { useAppEnvironment } from "@/contexts/AppEnvironmentContext";
import { AppIcon } from "@/components/generic/AppIcon";
import { NoAccessMessage } from "@/components/generic/NoAccessMessage";

import { ActiveTravelActionDrawer } from "@/components/features/Travels/ActiveTravelActionDrawer";
import { ActiveTravelIndicator } from "@/components/features/Travels/ActiveTravelIndicator";
import { ActiveParkingBanner } from "@/components/features/ParkingSession/ActiveParkingBanner";
import { ActiveParkingDetailDrawer } from "@/components/features/ParkingSession/ActiveParkingDetailDrawer";
import { SupportDrawer } from "@/components/features/Support/SupportDrawer";
import { StartParkingDrawer } from "@/components/features/ParkingSession/StartParkingDrawer";
import { ActiveTrackingBanner } from "@/components/features/Travels/ActiveTrackingBanner";

import { DesktopSidebar } from "./DesktopSidebar";
import { BottomTabBar } from "./BottomTabBar";
import { MoreView } from "./MoreView";
import { SubscriptionFooter, useSubscriptionFooter } from "./SubscriptionFooter";
import { PWAInstallBanner } from "./PWAInstallBanner";
import { PWAUpdateBanner } from "./PWAUpdateBanner";

import {
  FooterMessageType,
  SIDEBAR_WIDTH,
  DEFAULT_ICON_COLOR,
  getIconBgColor,
} from "./constants";

// ============================================
// Types
// ============================================

export interface MobileHeaderAction {
  /** Tabler icon name */
  iconName: string;
  /** Tooltip text */
  tooltip?: string;
  /** Button variant — controls styling */
  variant?: "default" | "subtle" | "filled";
  /** Whether action is disabled */
  disabled?: boolean;
  /** Click handler */
  onClick: () => void;
}

interface DashboardLayoutProps {
  children: React.ReactNode;
  page: any;
  // page: {
  //   name?: string;
  //   subtitle?: string;
  //   icon?: string;
  //   iconColor?: string;
  //   roles?: string[];
  //   translationKey?: string;
  // };
  footerMessage?: string;
  footerMessageType?: FooterMessageType;
  showFooterMessage?: boolean;

  /** Action buttons displayed in the mobile header (right side) */
  mobileHeaderActions?: MobileHeaderAction[];
}

// ============================================
// Constants
// ============================================

/** Height of the bottom tab bar + safe area + extra padding for content offset */
const BOTTOM_TAB_BAR_OFFSET = 90;

// ============================================
// Page Title Animation Variants
// ============================================

const titleEase = [0.22, 0.68, 0.35, 1] as const;

const titleVariants = {
  initial: {
    opacity: 0,
    y: -12,
    filter: "blur(3px)",
  },
  animate: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: {
      duration: 0.3,
      ease: titleEase,
    },
  },
  exit: {
    opacity: 0,
    y: 10,
    filter: "blur(3px)",
    transition: {
      duration: 0.18,
      ease: titleEase,
    },
  },
};

// ============================================
// Main Component
// ============================================

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  children,
  page,
  footerMessage,
  footerMessageType = "info",
  showFooterMessage = false,
  mobileHeaderActions,
}) => {
  const theme = useMantineTheme();
  const { t } = useTranslation(["common"]);
  const { isMobile } = useAppEnvironment();

  // Desktop sidebar state
  const [desktopCollapsed, { toggle: toggleDesktopSidebar }] =
    useDisclosure(false);

  // Mobile "More" view state
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [isMoreClosing, setIsMoreClosing] = useState(false);

  // Close More view when the route changes (not immediately on click).
  // This prevents a flash of the previous page while the new one loads.
  const pathnameRef = React.useRef(window.location.pathname);

  React.useEffect(() => {
    if (pathnameRef.current !== location.pathname) {
      pathnameRef.current = location.pathname;

      if (isMoreOpen) {
        // Start fade-out, then fully close after transition
        setIsMoreClosing(true);
        const timer = setTimeout(() => {
          setIsMoreOpen(false);
          setIsMoreClosing(false);
        }, 200);
        return () => clearTimeout(timer);
      }
    }

    return undefined;
  }, [window.location.pathname, isMoreOpen]);

  // Scroll position for mobile header border reveal
  const [scroll] = useWindowScroll();
  const isScrolled = scroll.y > 10;

  // Support drawer state
  const [
    supportDrawerOpened,
    { open: openSupportDrawer, close: closeSupportDrawer },
  ] = useDisclosure(false);

  // Active travel action drawer — single unified drawer
  const [
    activeTravelActionOpened,
    { open: openActiveTravelAction, close: closeActiveTravelAction },
  ] = useDisclosure(false);

  const { parkingSession: layoutParkingSession, setParkingSession } =
    useLayoutUI();

  // Parking drawer state derived from LayoutUI context
  const isParkingStartDrawerOpen =
    layoutParkingSession != null && !layoutParkingSession.id;
  const isParkingDetailDrawerOpen =
    layoutParkingSession != null && !!layoutParkingSession.id;

  const navigate = useNavigate();
  const location = useLocation();

  // Get filters from context to preserve them during navigation
  const { filters } = useEntityFiltersContext();

  const {
    user,
    userRoles,
    getVisibleRoutes,
    signOut,
    subscriptionStatus,
    trialEndsAt,
  } = useApp();

  // Active travel context
  const {
    activeTravel,
    hasActiveTravel,
    trackingSession,
    pauseTracking,
    resumeTracking,
    isPausingTracking,
    isResumingTracking,
    refresh: refreshActiveTravel,
  } = useActiveTravelContext();

  // Active parking context
  const {
    activeSessions: activeParkingSessions,
    hasActiveSessions: hasActiveParking,
    isEnding: isParkingEnding,
  } = useActiveParkingContext();


  const { reset: resetDashboard } = useDashboardContext();
  const { reset: resetExpenseTags } = useExpenseTagsContext();
  const { reset: resetNotifications } = useUserNotificationsContext();
  const { reset: resetUserProfile } = useUserProfileContext();

  const navigationItems = getVisibleRoutes();
  const { name, subtitle, options } = page;
  const { translationKey, icon, iconColor } = options || {};

  const hasAccess = canUserAccess(page.roles, userRoles);

  // Resolve page icon color
  const pageIconColor = iconColor || DEFAULT_ICON_COLOR;

  // App version
  const appVersion = `v${import.meta.env.VITE_APP_VERSION}`;

  // Is user an owner?
  const isOwner = user?.userRole?.id === USER_ROLES.OWNER;

  const showTrackingBanner =
    trackingSession.status === "active" ||
    trackingSession.status === "paused" ||
    trackingSession.status === "restoring";

  // ============================================
  // Active Travel Handlers
  // ============================================

  /** Indicator click → open the unified action drawer directly */
  const handleActiveTravelIndicatorClick = useCallback(() => {
    openActiveTravelAction();
  }, [openActiveTravelAction]);

  /** After completing a trip — refresh active travel state */
  const handleTravelCompleted = useCallback(async () => {
    await refreshActiveTravel();
  }, [refreshActiveTravel]);

  /** After adding a waypoint — refresh active travel state */
  const handleWaypointAdded = useCallback(async () => {
    await refreshActiveTravel();
  }, [refreshActiveTravel]);

  const handleTrackingPause = useCallback(async () => {
    await pauseTracking();
  }, [pauseTracking]);

  const handleTrackingResume = useCallback(async () => {
    await resumeTracking();
  }, [resumeTracking]);

  // ============================================
  // Active Parking Handlers
  // ============================================

  const handleParkingViewDetails = useCallback(
    (session: ParkingSession) => {
      setParkingSession(session);
    },
    [setParkingSession]
  );

  // ============================================
  // Translation Helpers
  // ============================================

  const getTranslatedName = useCallback(
    (itemTranslationKey?: string, fallbackName?: string): string => {
      if (itemTranslationKey) {
        const translated = t(`common:pages.${itemTranslationKey}.name`, {
          defaultValue: "",
        });
        if (translated) return translated;
      }
      return fallbackName || "";
    },
    [t]
  );

  const getTranslatedSubtitle = useCallback(
    (itemTranslationKey?: string, fallbackSubtitle?: string): string => {
      if (itemTranslationKey) {
        const translated = t(`common:pages.${itemTranslationKey}.subtitle`, {
          defaultValue: "",
        });
        if (translated) return translated;
      }
      return fallbackSubtitle || "";
    },
    [t]
  );

  // Get translated current page name and subtitle
  const translatedName = getTranslatedName(translationKey, name);
  const translatedSubtitle = getTranslatedSubtitle(translationKey, subtitle);

  // Stable animation key — use pathname so transitions trigger on route changes
  const titleAnimationKey = location.pathname;

  // ============================================
  // Navigation Handlers
  // ============================================

  const handleNavClick = useCallback(
    (path: string) => {
      // Special sentinel for support drawer
      if (path === "__support__") {
        setIsMoreOpen(false);
        openSupportDrawer();
        return;
      }

      const targetPath = buildUrlWithFilters(path, filters);
      navigate(targetPath);

      // Don't close More view here — useEffect on location.pathname
      // will close it once the new page actually renders, avoiding
      // a flash of the previous page content.
    },
    [filters, navigate, openSupportDrawer]
  );

  const handleSignOut = useCallback(() => {
    setIsMoreOpen(false);
    resetDashboard();
    resetExpenseTags();
    resetNotifications();
    resetUserProfile();
    signOut();
    navigate("/account/signin");
  }, [
    resetDashboard,
    resetExpenseTags,
    resetNotifications,
    resetUserProfile,
    signOut,
    navigate,
  ]);

  // ============================================
  // Bottom Tab Bar Handlers
  // ============================================

  const handleTabNavigate = useCallback(
    (path: string) => {
      // If More is open and user taps a tab, close More immediately
      // since we're navigating away from More to a primary tab.
      setIsMoreOpen(false);
      setIsMoreClosing(false);
      const targetPath = buildUrlWithFilters(path, filters);
      navigate(targetPath);
    },
    [filters, navigate]
  );

  const handleMoreClick = useCallback(() => {
    setIsMoreOpen((prev) => !prev);
  }, []);

  const handleCloseMore = useCallback(() => {
    setIsMoreClosing(true);
    const timer = setTimeout(() => {
      setIsMoreOpen(false);
      setIsMoreClosing(false);
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  // ============================================
  // Subscription Footer
  // ============================================

  const { hasFooter } = useSubscriptionFooter({
    subscriptionStatus,
    trialEndsAt,
    showFooterMessage,
  });

  // ============================================
  // Navigation Helpers
  // ============================================

  const isItemActive = useCallback(
    (item: SiteMapItem): boolean => {
      if (item.url && location.pathname === item.url) return true;
      if (item.items?.length) return item.items.some(isItemActive);
      return false;
    },
    [location.pathname]
  );

  // Items for MoreView (everything after the first 4)
  const moreViewItems = useMemo(() => {
    return navigationItems.filter((item: any) => !Boolean(item.bottomNavBar));
  }, [navigationItems]);

  // Desktop sidebar needs shouldShowSidebarContent
  const shouldShowSidebarContent = !isMobile && !desktopCollapsed;

  // ============================================
  // Render
  // ============================================

  return (
    <>
      <AppShell
        navbar={{
          width: desktopCollapsed ? 0 : SIDEBAR_WIDTH,
          breakpoint: "sm",
          collapsed: {
            mobile: true, // Always collapsed on mobile — we use BottomTabBar
            desktop: desktopCollapsed,
          },
        }}
        footer={hasFooter && !isMobile ? { height: { base: 90, sm: 60 } } : undefined}
        padding={0}
        styles={{
          main: {
            backgroundColor: theme.colors.neutral[0],
            display: "flex",
            flexDirection: "column",
            minHeight: "100vh",
            paddingTop: isMobile ? 60 : 0,
            paddingBottom: isMobile ? BOTTOM_TAB_BAR_OFFSET : 0,
          },
          navbar: {
            borderRight: `1px solid ${theme.colors.neutral[2]}`,
            backgroundColor: theme.colors.neutral[0],
          },
          footer: {
            borderTop: `1px solid ${theme.colors.neutral[2]}`,
          },
        }}
      >
        {/* Mobile Header */}
        <Box
          pos="fixed"
          top={0}
          left={0}
          right={0}
          h={60}
          bg="neutral.0"
          hiddenFrom="sm"
          style={{
            borderBottom: isScrolled
              ? `1px solid ${theme.colors.neutral[2]}`
              : "1px solid transparent",
            zIndex: 100,
            transition: "border-color 200ms ease",
          }}
        >
          <Group h="100%" px="md" justify="space-between">
            {/* Animated mobile title */}
            <Box style={{ flex: 1, minWidth: 0, overflow: "hidden", position: "relative" }}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={isMoreOpen ? "__more__" : titleAnimationKey}
                  variants={titleVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                >
                  <Title order={3} c="neutral.9" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {isMoreOpen
                      ? t("navigation.more", { defaultValue: "More" })
                      : translatedName}
                  </Title>
                </motion.div>
              </AnimatePresence>
            </Box>

            <Group gap="md" wrap="nowrap" style={{ flexShrink: 0 }}>
              {/* Active travel indicator — opens unified action drawer */}
              {!isMoreOpen && hasActiveTravel && (
                <ActiveTravelIndicator
                  onClick={handleActiveTravelIndicatorClick}
                  startTime={activeTravel?.firstDttm}
                  variant="mobile"
                />
              )}

              {/* Page-specific action buttons */}
              {!isMoreOpen &&
                mobileHeaderActions?.map((action, index) => (
                  <Tooltip
                    key={`header-action-${index}`}
                    label={action.tooltip}
                    disabled={!action.tooltip}
                    position="bottom"
                    withArrow
                    openDelay={500}
                  >
                    <ActionIcon
                      variant={action.variant || "default"}
                      size={40}
                      radius="md"
                      disabled={action.disabled}
                      onClick={action.onClick}
                      color={action.variant === "filled" ? "brand" : undefined}
                      style={{
                        border:
                          action.variant === "default" || !action.variant
                            ? `1px solid ${theme.colors.neutral[2]}`
                            : undefined,
                      }}
                    >
                      <AppIcon iconName={action.iconName} size={32} />
                    </ActionIcon>
                  </Tooltip>
                ))}
            </Group>
          </Group>
        </Box>

        {/* Desktop Sidebar */}
        <AppShell.Navbar>
          {shouldShowSidebarContent && (
            <DesktopSidebar
              navigationItems={navigationItems}
              user={user}
              userInitials={
                user?.username
                  ?.split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase() || "?"
              }
              isOwner={isOwner}
              appVersion={appVersion}
              currentPath={location.pathname}
              onNavClick={handleNavClick}
              onToggleCollapse={toggleDesktopSidebar}
              onSignOut={handleSignOut}
              onOpenSupportDrawer={openSupportDrawer}
              getTranslatedName={getTranslatedName}
              isItemActive={isItemActive}
              t={t}
            />
          )}
        </AppShell.Navbar>

        {/* Desktop Footer */}
        {hasFooter && !isMobile && (
          <AppShell.Footer>
            <SubscriptionFooter
              subscriptionStatus={subscriptionStatus}
              trialEndsAt={trialEndsAt}
              footerMessage={footerMessage}
              footerMessageType={footerMessageType}
              showFooterMessage={showFooterMessage}
            />
          </AppShell.Footer>
        )}

        {/* Main Content */}
        <AppShell.Main>
          <PWAInstallBanner />
          <PWAUpdateBanner />

          {/* Page Header - Desktop */}
          {!isMobile && (
            <Box
              p={{ base: "md", sm: "xl" }}
              pb={{ base: "sm", sm: "md" }}
              bg="white"
              style={{ borderBottom: `1px solid ${theme.colors.neutral[1]}` }}
            >
              <Group justify="space-between" align="flex-start">
                <Group gap="md" align="center" style={{ flex: 1, minWidth: 0 }}>
                  {desktopCollapsed && (
                    <Tooltip label="Expand sidebar" position="right" withArrow>
                      <ActionIcon
                        variant="default"
                        size="lg"
                        onClick={toggleDesktopSidebar}
                        aria-label="Expand sidebar"
                        style={{
                          border: `1px solid ${theme.colors.neutral[2]}`,
                          flexShrink: 0,
                        }}
                      >
                        <AppIcon iconName="Menu" size={18} />
                      </ActionIcon>
                    </Tooltip>
                  )}

                  {/* Animated desktop page icon */}
                  <AnimatePresence mode="wait">
                    {icon && (
                      <motion.div
                        key={`icon-${titleAnimationKey}`}
                        initial={{ opacity: 0, scale: 0.85, y: -8 }}
                        animate={{
                          opacity: 1,
                          scale: 1,
                          y: 0,
                          transition: { duration: 0.3, ease: titleEase },
                        }}
                        exit={{
                          opacity: 0,
                          scale: 0.85,
                          y: 8,
                          transition: { duration: 0.18, ease: titleEase },
                        }}
                      >
                        <Box
                          w={{ base: 38, sm: 44 }}
                          h={{ base: 38, sm: 44 }}
                          style={{
                            backgroundColor: getIconBgColor(pageIconColor),
                            borderRadius: theme.radius.md,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <AppIcon
                            iconName={icon}
                            size={28}
                            color={pageIconColor}
                          />
                        </Box>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Animated desktop title + subtitle */}
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={titleAnimationKey}
                      variants={titleVariants}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                      style={{ minWidth: 0 }}
                    >
                      <Box>
                        <Title
                          order={1}
                          fz={{ base: "1.375rem", sm: "1.75rem" }}
                          fw={700}
                          c="neutral.8"
                          lh={1.2}
                        >
                          {translatedName}
                        </Title>
                        {translatedSubtitle && (
                          <Text size="sm" c="neutral.5" mt={4} lh={1.4}>
                            {translatedSubtitle}
                          </Text>
                        )}
                      </Box>
                    </motion.div>
                  </AnimatePresence>
                </Group>

                {hasActiveTravel && (
                  <ActiveTravelIndicator
                    onClick={handleActiveTravelIndicatorClick}
                    startTime={activeTravel?.firstDttm}
                    carLabel={activeTravel?.car?.label}
                    variant="desktop"
                  />
                )}
              </Group>
            </Box>
          )}

          {/* Active Parking Banner */}
          {hasActiveParking && !isMoreOpen && (
            <Box px={{ base: "md", sm: "xl" }} pt="sm">
              <ActiveParkingBanner
                activeSessions={activeParkingSessions}
                onTap={handleParkingViewDetails}
                loading={isParkingEnding}
              />
            </Box>
          )}

          {/* Active Tracking Banner */}
          {showTrackingBanner && !isMoreOpen && (
            <Box px={{ base: "md", sm: "xl" }} pt="sm">
              <ActiveTrackingBanner
                session={trackingSession}
                onTap={handleActiveTravelIndicatorClick}
                onPause={handleTrackingPause}
                onResume={handleTrackingResume}
                isPausing={isPausingTracking}
                isResuming={isResumingTracking}
              />
            </Box>
          )}

          {/* Page Content or More View */}
          {isMobile && isMoreOpen ? (
            <Box
              style={{
                opacity: isMoreClosing ? 0 : 1,
                transition: "opacity 200ms ease",
              }}
            >
              <MoreView
                navigationItems={moreViewItems}
                user={user}
                isOwner={isOwner}
                appVersion={appVersion}
                currentPath={location.pathname}
                onNavigate={handleNavClick}
                onClose={handleCloseMore}
                onSignOut={handleSignOut}
                getTranslatedName={getTranslatedName}
                getTranslatedSubtitle={getTranslatedSubtitle}
              />
            </Box>
          ) : (
            <Box flex={1} p={{ base: "md", sm: "xl" }} bg="neutral.0">
              {!hasAccess ? <NoAccessMessage /> : children}
            </Box>
          )}
        </AppShell.Main>
      </AppShell>

      {/* Bottom Tab Bar - Mobile Only */}
      {isMobile && (
        <BottomTabBar
          navigationItems={navigationItems}
          currentPath={location.pathname}
          onNavigate={handleTabNavigate}
          isMoreActive={isMoreOpen}
          onMoreClick={handleMoreClick}
          isItemActive={isItemActive}
          getTranslatedName={getTranslatedName}
        />
      )}

      {/* Unified Active Travel Action Drawer
          Replaces: ActiveTravelDrawer + StopTravelDrawer + AddWaypointDrawer */}
      <ActiveTravelActionDrawer
        opened={activeTravelActionOpened}
        onClose={closeActiveTravelAction}
        activeTravel={activeTravel}
        onWaypointAdded={handleWaypointAdded}
        onTravelCompleted={handleTravelCompleted}
      />

      {/* Support Drawer */}
      <SupportDrawer
        opened={supportDrawerOpened}
        onClose={closeSupportDrawer}
      />

      {/* Start Parking Drawer */}
      <StartParkingDrawer
        opened={isParkingStartDrawerOpen}
        onClose={() => setParkingSession(null)}
        defaultCarId={layoutParkingSession?.carId || null}
      />

      {/* Active Parking Detail Drawer */}
      <ActiveParkingDetailDrawer
        opened={isParkingDetailDrawerOpen}
        onClose={() => setParkingSession(null)}
        session={layoutParkingSession}
      />
    </>
  );
};